import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Form, Row, Col, Spinner } from 'react-bootstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';
import AsyncSelect from 'react-select/async';
import { toast } from 'react-toastify';
import { FaMapMarkerAlt } from 'react-icons/fa';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import { ESTADOS } from '~/constant';
import * as api from '~/Services/Service';

var validationSchema = Yup.object().shape({
  descricao: Yup.string().required('Descrição é obrigatória').max(100),
  endereco: Yup.string().max(40).nullable(),
  numero: Yup.string().max(10).nullable(),
  complemento: Yup.string().max(30).nullable(),
  bairro: Yup.string().max(40).nullable(),
  estado: Yup.string().nullable(),
  codigo_cidade: Yup.number().nullable(),
  telefone: Yup.string().max(11).matches(/^\d*$/, 'Apenas números').nullable(),
});

var sectionLabelStyle = {
  fontSize: '0.8rem',
  fontWeight: 600,
  color: '#0747a6',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  marginBottom: 8,
  marginTop: 4,
  borderBottom: '1px solid #e9ecef',
  paddingBottom: 4,
};

var ModalPdv = function (props) {
  var show = props.show;
  var onHide = props.onHide;
  var pdv = props.pdv;
  var onSalvar = props.onSalvar;

  var [cidades, setCidades] = useState([]);
  var [cidadesById, setCidadesById] = useState({});
  var [loadingCidades, setLoadingCidades] = useState(false);
  var timeoutRef = useRef(null);

  useEffect(function () {
    if (show && pdv && pdv.estado) {
      carregarCidades(pdv.estado);
    }
    if (!show) {
      setCidades([]);
      setCidadesById({});
    }
  }, [show]);

  var carregarCidades = async function (uf) {
    if (!uf) {
      setCidades([]);
      setCidadesById({});
      return;
    }
    setLoadingCidades(true);
    try {
      var resp = await api.getCidades(uf);
      var lista = resp || [];
      setCidades(lista);
      var byId = {};
      lista.forEach(function (c) { byId[c.codigo] = c; });
      setCidadesById(byId);
    } catch (e) {
      setCidades([]);
      setCidadesById({});
    } finally {
      setLoadingCidades(false);
    }
  };

  var removerAcentos = function (str) {
    return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  };

  var filtroCidades = function (inputValue) {
    var termo = removerAcentos(inputValue.toLowerCase());
    return cidades
      .filter(function (c) {
        return removerAcentos(c.descricao.toLowerCase()).indexOf(termo) >= 0;
      })
      .map(function (c) {
        return { value: c.codigo, label: c.descricao };
      });
  };

  var carregarCidadesAsync = function (inputValue, callback) {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(function () {
      callback(filtroCidades(inputValue));
    }, 500);
  };

  var getCidadeValue = function (codigoCidade) {
    if (codigoCidade == null) return { value: '', label: cidades.length === 0 ? 'Selecione o estado' : 'Selecione uma cidade' };
    var found = cidadesById[codigoCidade];
    if (found) return { value: found.codigo, label: found.descricao };
    return { value: '', label: 'Selecione uma cidade' };
  };

  var getInitialValues = function () {
    if (pdv) {
      return {
        descricao: pdv.descricao || '',
        endereco: pdv.endereco || '',
        numero: pdv.numero || '',
        complemento: pdv.complemento || '',
        bairro: pdv.bairro || '',
        estado: pdv.estado || '',
        codigo_cidade: pdv.codigo_cidade != null ? pdv.codigo_cidade : null,
        telefone: pdv.telefone != null ? String(pdv.telefone) : '',
      };
    }
    return {
      descricao: '',
      endereco: '',
      numero: '',
      complemento: '',
      bairro: '',
      estado: '',
      codigo_cidade: null,
      telefone: '',
    };
  };

  var handleSubmit = async function (values, formikHelpers) {
    try {
      if (pdv) {
        await api.atualizarPdv({
          codigo_pdv: pdv.codigo_pdv,
          descricao: values.descricao,
          endereco: values.endereco || null,
          numero: values.numero || null,
          complemento: values.complemento || null,
          bairro: values.bairro || null,
          codigo_cidade: values.codigo_cidade != null ? values.codigo_cidade : null,
          telefone: values.telefone || null,
        });
        toast.success('PDV atualizado com sucesso.');
      } else {
        await api.criarPdv({
          descricao: values.descricao,
          endereco: values.endereco || null,
          numero: values.numero || null,
          complemento: values.complemento || null,
          bairro: values.bairro || null,
          codigo_cidade: values.codigo_cidade != null ? values.codigo_cidade : null,
          telefone: values.telefone || null,
        });
        toast.success('PDV cadastrado com sucesso.');
      }
      onSalvar();
    } catch (err) {
      toast.error((err && err.message) || 'Erro ao salvar PDV.');
    } finally {
      formikHelpers.setSubmitting(false);
    }
  };

  var titulo = pdv ? 'Editar PDV #' + pdv.codigo_pdv : 'Novo PDV';

  return (
    <Formik
      initialValues={getInitialValues()}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {function (formik) {
        var values = formik.values;
        var errors = formik.errors;
        var touched = formik.touched;
        var setFieldValue = formik.setFieldValue;
        var handleChange = formik.handleChange;
        var handleSubmit = formik.handleSubmit;
        var isSubmitting = formik.isSubmitting;

        return (
          <Modal show={show} onHide={onHide} backdrop="static" size="lg">
            <Modal.Header closeButton style={{ backgroundColor: '#0747a6', color: '#fff' }}>
              <Modal.Title style={{ fontSize: '1rem', color: '#fff' }}>{titulo}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {isSubmitting && (
                <div className="d-flex align-items-center mb-3" aria-live="polite">
                  <Spinner animation="border" size="sm" role="status" className="mr-2" />
                  <span>Salvando...</span>
                </div>
              )}
              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>Descrição <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    name="descricao"
                    value={values.descricao}
                    onChange={handleChange}
                    maxLength={100}
                    placeholder="Nome ou título do PDV"
                    isInvalid={touched.descricao && !!errors.descricao}
                    autoFocus
                  />
                  <Form.Control.Feedback type="invalid">{errors.descricao}</Form.Control.Feedback>
                </Form.Group>

                <div style={sectionLabelStyle}>
                  <FaMapMarkerAlt style={{ marginRight: 4 }} /> Endereço
                </div>

                <Row>
                  <Col md={8}>
                    <Form.Group className="mb-3">
                      <Form.Label>Logradouro</Form.Label>
                      <Form.Control
                        name="endereco"
                        value={values.endereco}
                        onChange={handleChange}
                        maxLength={40}
                        placeholder="Rua, Avenida..."
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Número</Form.Label>
                      <Form.Control
                        name="numero"
                        value={values.numero}
                        onChange={function (e) {
                          var val = e.target.value.replace(/\D/g, '').slice(0, 10);
                          setFieldValue('numero', val);
                        }}
                        maxLength={10}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Complemento</Form.Label>
                      <Form.Control
                        name="complemento"
                        value={values.complemento}
                        onChange={handleChange}
                        maxLength={30}
                        placeholder="Sala, Bloco..."
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Bairro</Form.Label>
                      <Form.Control
                        name="bairro"
                        value={values.bairro}
                        onChange={handleChange}
                        maxLength={40}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="align-items-end">
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Estado</Form.Label>
                      <Form.Control
                        as="select"
                        name="estado"
                        value={values.estado}
                        onChange={function (e) {
                          var novoUf = e.target.value;
                          handleChange(e);
                          setFieldValue('codigo_cidade', null);
                          carregarCidades(novoUf);
                        }}
                      >
                        <option value="">Selecione</option>
                        {ESTADOS.map(function (item) {
                          return <option key={item.value} value={item.value}>{item.label}</option>;
                        })}
                      </Form.Control>
                    </Form.Group>
                  </Col>
                  <Col md={5}>
                    <Form.Group className="mb-3">
                      <Form.Label>Cidade</Form.Label>
                      <AsyncSelect
                        isDisabled={!values.estado || cidades.length === 0}
                        isLoading={loadingCidades}
                        loadingMessage={function () { return 'Carregando...'; }}
                        noOptionsMessage={function () { return 'Digite uma cidade...'; }}
                        placeholder={!values.estado ? 'Selecione o estado' : 'Pesquisar cidades...'}
                        loadOptions={carregarCidadesAsync}
                        defaultOptions={cidades.slice(0, 20).map(function (c) { return { value: c.codigo, label: c.descricao }; })}
                        onChange={function (option) {
                          setFieldValue('codigo_cidade', option ? option.value : null);
                        }}
                        isClearable
                        value={getCidadeValue(values.codigo_cidade)}
                        components={{ DropdownIndicator }}
                        styles={customStyles}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Telefone</Form.Label>
                      <Form.Control
                        name="telefone"
                        value={values.telefone}
                        onChange={function (e) {
                          var val = e.target.value.replace(/\D/g, '').slice(0, 11);
                          setFieldValue('telefone', val);
                        }}
                        maxLength={11}
                        placeholder="DDD + número"
                        isInvalid={touched.telefone && !!errors.telefone}
                      />
                      <Form.Control.Feedback type="invalid">{errors.telefone}</Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>
              </Form>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={onHide} disabled={isSubmitting}>
                Cancelar
              </Button>
              <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting}>
                {isSubmitting ? 'Salvando...' : 'Salvar'}
              </Button>
            </Modal.Footer>
          </Modal>
        );
      }}
    </Formik>
  );
};

export default ModalPdv;
